/**
 * Translate technical error messages into plain-English explanations.
 *
 * Withdraw / deposit / AM-claim flows produce a small set of recurring
 * technical errors — "Leaf not present in tree", on-chain `ERR_*`
 * codes, relayer 5xx, fetch failures. Surfacing those verbatim to a
 * non-technical user reads as "the app is broken." We map each known
 * pattern to a one-sentence explanation up front, then append the
 * technical detail so developers can still debug.
 *
 * The shape we return is `"<friendly>\n\nTechnical detail: <raw>"`. The
 * UI renders the first line prominently and indents the technical
 * detail.
 */

interface Mapping {
  match: RegExp;
  friendly: string;
}

// Order matters: first match wins. Put the more specific patterns first.
//
// Patterns are written to match POSITIVE assertions only — never the
// word "spent" inside a hint like "isn't already spent" (which appears
// in our own curated relayer 400-body). Each `spent` matcher requires
// the word `nullifier` adjacent and the literal `already`, with no
// negation tokens, so a checklist-style message can't trip them.
const MAPPINGS: Mapping[] = [
  {
    match: /leaf not present in tree/i,
    friendly:
      "This note hasn't been deposited yet — or the relayer hasn't indexed the deposit. " +
      "If you just deposited, wait ~30 seconds and try again. Otherwise, double-check that you pasted the correct note.",
  },
  {
    // Positive assertions that the nullifier IS spent. Constructed so a
    // negative hint like "the nullifier isn't already spent" can't match.
    match:
      /(ERR_ALREADY_SPENT|already been (withdrawn|spent)|note has been (withdrawn|spent)|nullifier .{0,30}is already spent)/i,
    friendly: "This note has already been withdrawn.",
  },
  {
    match: /ERR_UNKNOWN_ROOT/i,
    friendly:
      "The pool no longer recognises the Merkle root your proof was built against. " +
      "Refresh the page and try the withdraw again — the tree has advanced since the proof was generated.",
  },
  {
    match: /(fee.*too low|below minimum|ERR_FEE_EXCEEDS_DENOM)/i,
    friendly:
      "The relayer's minimum fee is higher than the fee you offered. " +
      "Try again with a higher fee, or pick a different relayer.",
  },
  {
    match: /ERR_PROOF_INVALID/i,
    friendly:
      "The zero-knowledge proof was rejected by the pool contract. " +
      "This usually means the proof was built against a stale Merkle root — refresh and try again.",
  },
  {
    match: /(insufficient (TON|gas|balance).*relayer|relayer.*insufficient)/i,
    friendly:
      "The relayer's wallet doesn't have enough TON to broadcast your withdraw. " +
      "Try a different relayer or contact the operator.",
  },
  {
    match: /internal server error/i,
    friendly:
      "The relayer ran into an unexpected issue. Try again in a moment, or pick a different relayer if the problem persists.",
  },
  {
    match: /(Failed to fetch|NetworkError|ECONNREFUSED|ETIMEDOUT)/i,
    friendly:
      "We couldn't reach the relayer. Check your internet connection and try again, or pick a different relayer.",
  },
  {
    match: /(429|too many requests|rate limit|ratelimit)/i,
    friendly:
      "We're being rate-limited by the TON RPC provider. Try again in a moment. " +
      "If this happens often, the dApp or relayer operator needs to configure a TON RPC API key.",
  },
  {
    match: /could not derive note|invalid note|note.*malformed/i,
    friendly:
      "We couldn't read that deposit note. Make sure you copied the whole string — it should start with `tonado-` and end with a 64-character hex secret.",
  },
  {
    // Catch-all for our own relayer's curated rejection strings. The
    // backend already wrote a safe-to-show, user-facing checklist — use
    // it verbatim so we don't double-translate (and risk false-matching
    // a negation like "isn't already spent").
    match: /(withdraw|AM claim) rejected — /,
    friendly:
      "The relayer rejected the request. Possible causes are listed below — try a different relayer if the problem persists.",
  },
];

export interface FriendlyError {
  /** The plain-English message we want the user to read first. */
  friendly: string;
  /** The raw technical message, surfaced as supporting detail. */
  technical: string;
}

export function friendlyError(err: unknown): FriendlyError {
  const technical =
    err instanceof Error ? err.message : typeof err === "string" ? err : String(err);
  for (const m of MAPPINGS) {
    if (m.match.test(technical)) {
      return { friendly: m.friendly, technical };
    }
  }
  // Unmatched: present the raw text as the headline but with a soft
  // prefix so the user sees we tried, then keep the technical line so
  // they can copy/paste to a maintainer.
  return {
    friendly: "Something went wrong.",
    technical,
  };
}
